import { Injectable } from '@angular/core';
import {
  DelayedRecallResult,
  DigitSpanResult,
  ProfileResult,
  ReactionResult,
  SignalLevel,
  SignalsResult,
  ValidityLabel,
  WordFindingResult,
} from '../sharp-v2.types';

@Injectable({ providedIn: 'root' })
export class SharpScoringService {
  median(a: number[]): number {
    if (!a.length) return 0;
    const s = [...a].sort((x, y) => x - y);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }

  mean(a: number[]): number {
    return a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0;
  }

  stddev(a: number[]): number {
    if (a.length < 2) return 0;
    const m = this.mean(a);
    return Math.sqrt(a.reduce((s, v) => s + (v - m) ** 2, 0) / (a.length - 1));
  }

  randInt(a: number, b: number): number {
    return Math.floor(Math.random() * (b - a + 1)) + a;
  }

  pick<T>(a: T[]): T {
    return a[this.randInt(0, a.length - 1)];
  }

  round(n: number, d = 0): number {
    const f = 10 ** d;
    return Math.round(n * f) / f;
  }

  fuzzyMatch(input: string, target: string): boolean {
    const a = input.toLowerCase().trim();
    const b = target.toLowerCase().trim();
    if (a === b) return true;
    if (a.length < 2) return false;
    if (b.startsWith(a) && b.length - a.length <= 2) return true;
    if (a.startsWith(b) && a.length - b.length <= 1) return true;
    if (Math.abs(a.length - b.length) <= 1) {
      let d = 0;
      for (let i = 0; i < Math.max(a.length, b.length); i++) {
        if (a[i] !== b[i]) d++;
        if (d > 1) return false;
      }
      return true;
    }
    return false;
  }

  evaluateValidity(
    sessionStart: number,
    existingFlags: string[]
  ): { flags: string[]; label: ValidityLabel } {
    const flags = [...existingFlags];
    const elapsed = Date.now() - sessionStart;
    if (elapsed < 60000) flags.push('Session completed unusually quickly');
    const label: ValidityLabel =
      flags.length <= 1 ? 'high' : flags.length <= 2 ? 'moderate' : 'low';
    return { flags, label };
  }

  generateSignals(
    rt: ReactionResult,
    wf: WordFindingResult,
    ds: DigitSpanResult,
    dr: DelayedRecallResult
  ): SignalsResult {
    const flags: string[] = [];

    let speed: SignalLevel = 'stable';
    if (rt.cv > 35) {
      speed = 'strong';
      flags.push(`High processing variability (CV ${rt.cv}%)`);
    } else if (rt.cv > 28) {
      speed = 'elevated';
      flags.push('Elevated reaction inconsistency');
    } else if (rt.cv > 22) {
      speed = 'tracking';
    }
    if (rt.lapses >= 4) {
      flags.push(`Processing lapses: ${rt.lapses}`);
      if (speed === 'stable') speed = 'tracking';
    }

    let retrieval: SignalLevel = 'stable';
    if (wf.uniqueValid < 6) {
      retrieval = 'strong';
      flags.push(`Low word retrieval (${wf.uniqueValid})`);
    } else if (wf.uniqueValid < 9) {
      retrieval = 'elevated';
      flags.push('Below-average fluency');
    } else if (wf.uniqueValid < 12) {
      retrieval = 'tracking';
    }

    let wm: SignalLevel = 'stable';
    if (ds.maxForward <= 3) {
      wm = 'strong';
      flags.push('Low forward span');
    } else if (ds.maxBackward <= 2) {
      wm = 'elevated';
      flags.push('Low backward span');
    } else if (ds.backwardCost >= 3) {
      wm = 'elevated';
      flags.push('High backward cost');
    } else if (ds.backwardCost >= 2) {
      wm = 'tracking';
    }

    let recall: SignalLevel = 'stable';
    if (dr.freeScore === 0) {
      recall = 'strong';
      flags.push('No free recall');
    } else if (dr.freeScore <= 1 && dr.cuedScore <= 2) {
      recall = 'elevated';
      flags.push('Weak recall even with cues');
    } else if (dr.freeScore <= 1) {
      recall = 'tracking';
      flags.push('Low free recall, improved with cues');
    } else if (dr.freeScore <= 2) {
      recall = 'tracking';
    }

    return {
      retrieval,
      workingMemory: wm,
      processingSpeed: speed,
      delayedRecall: recall,
      flags,
    };
  }

  determineProfile(s: SignalsResult): ProfileResult {
    const retElev = s.retrieval === 'elevated' || s.retrieval === 'strong';
    const wmElev = s.workingMemory === 'elevated' || s.workingMemory === 'strong';
    const speedElev = s.processingSpeed === 'elevated' || s.processingSpeed === 'strong';
    const allStable =
      s.retrieval === 'stable' &&
      s.workingMemory === 'stable' &&
      s.processingSpeed === 'stable' &&
      s.delayedRecall === 'stable';
    const anyTracking =
      s.retrieval === 'tracking' ||
      s.workingMemory === 'tracking' ||
      s.processingSpeed === 'tracking' ||
      s.delayedRecall === 'tracking';

    if (allStable && !anyTracking) return { name: 'stable', description: 'stableDesc' };
    if (retElev && !wmElev && !speedElev) return { name: 'retrieval', description: 'retrievalDesc' };
    if (speedElev) return { name: 'variability', description: 'variabilityDesc' };
    if (wmElev && retElev) return { name: 'attentionLoad', description: 'attentionLoadDesc' };
    if (anyTracking && !retElev && !wmElev && !speedElev)
      return { name: 'emerging', description: 'emergingDesc' };
    return { name: 'mixed', description: 'mixedDesc' };
  }
}
